// ─── IMPORTS ───────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { ChevronUp, ChevronDown } from 'lucide-react';

// ─── GLOBAL COLOR CONFIG ─────────────────────────────────

export const tableColors = {
  header: 'var(--green-800)',
  accent: 'var(--orange-500)',
  text: 'var(--black)',
};

// ─── Radio Group ──────────────────────────

export const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const SizeTag = styled.span`
  background: var(--green-800);
  color: var(--white);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85rem;
  white-space: nowrap;
`;

export const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-body);
  color: var(--white);
  font-size: 0.95rem;
  background: var(--white-a15);
  padding: 6px 12px;
  border-radius: 12px;
  backdrop-filter: blur(8px);
  cursor: pointer;
  transition: all 0.2s ease;

  input[type='radio'] {
    accent-color: var(--black);
  }

  &:hover {
    background: var(--white-a20);
  }
`;

// ─── ZOOM WRAPPER ────────────────────────────────

export const ZoomWrapper = ({ children, zoom = '80%' }) => (
  <div style={{ zoom, transformOrigin: 'top center', width: '100%' }}>{children}</div>
);

// ─── SORTING ────────────────────────────

export const SortableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  text-align: left;
  background-color: ${tableColors.header};
  color: var(--white);
  font-family: var(--font-heading);
  border-bottom: 1px solid var(--white-a20);
  white-space: nowrap;

  &:first-child {
    border-top-left-radius: 12px;
  }

  &:last-child {
    border-top-right-radius: 12px;
  }

  svg {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.3;
    transition: opacity 0.2s ease;
  }

  &:hover svg {
    opacity: 0.6;
  }

  &:hover {
    background-color: var(--green-900);
  }
`;

export const SortArrow = ({ direction }) =>
  direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;

// ─── TABLE LAYOUT WRAPPERS ────────────────────

export const TableContainer = styled.div`
  border-radius: 16px;
  background: var(--white-a25);
  backdrop-filter: blur(10px);
  border: 1px solid var(--white-a15);
  box-shadow: 0 8px 32px var(--shadow-a08);
  padding: 1rem;
  margin-bottom: 2rem;
`;

export const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--green-800);
  margin-bottom: 1.5rem;
  text-shadow: 0 1px 1px var(--shadow-a05);
`;

export const Container = styled.div`
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden; /* prevent viewport sideways scroll */
`;

export const TableWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: auto;
  scroll-behavior: smooth;
  position: relative; /* for scroll fade affordance */
  /* Subtle edge fade to hint horizontal scroll */
  &::after {
    content: '';
    position: sticky;
    right: 0;
    top: 0;
    width: 24px;
    height: calc(100% - 1px);
    pointer-events: none;
    background: linear-gradient(to left, var(--shadow-a08), transparent);
    display: block;
  }
`;



export const Table = styled.table`
  width: 100%;
  max-width: 100%;
  min-width: 100%; /* allow table to grow wider and scroll inside wrapper */
  border-collapse: collapse;
  table-layout: auto;
  font-family: var(--font-body);
  border-radius: 16px;
  overflow: hidden;
`;


export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const Th = styled.th`
  background-color: ${tableColors.header};
  color: var(--white);
  font-weight: 600;
  padding: 0.5rem 0.5rem;
  font-size: 0.8rem;
  text-align: left;
  font-family: var(--font-heading);
  border-bottom: 1px solid var(--white-a20);
`;

export const Td = styled.td`
  padding: 0.5rem;
  text-align: ${({ numeric }) => (numeric ? 'right' : 'left')};
  vertical-align: middle;
  font-family: var(--font-body);
  background: var(--white-a20);
  backdrop-filter: blur(6px);
  color: ${tableColors.text};
  border-top: 1px solid var(--white-a25);
  border-bottom: 1px solid var(--white-a25);
  /* Ensure child inputs/selects don't overflow the fixed width cell */
  & > * {
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
  }
`;

export const StyledTr = styled.tr`
  transition: all 0.3s ease;
  background: ${({ $index }) =>
    $index % 2
      ? 'var(--white-a25)'
      : 'var(--white-a15)'};

  &:hover {
    background-color: var(--white-a40);
  }

  /* Only ever visible on rows a page makes focusable (tabIndex) for row-click. */
  &:focus-visible {
    outline: 2px solid var(--green-800);
    outline-offset: -2px;
  }
`;


export const Tag = styled.span`
  background: var(--green-a08);
  color: var(--green-800);
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.8rem;
  display: inline-block;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: var(--green-a15);
    color: var(--white);
  }
`;

// ─── INPUTS / BUTTONS ───────────────────────────────────────────────────────────

export const Button = styled.button`
  background-color: ${tableColors.header};
  color: var(--white);
  border: none;
  padding: 5px 12px;
  font-size: 0.75rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  font-family: var(--font-body);
  font-weight: 500;
  cursor: pointer;
  width: auto;
  min-width: 60px;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled(Button)`
  background-color: var(--red-600);
  margin-left: 0.5rem;

  &:hover {
    background-color: var(--color-danger);
  }
`;

export const SecondaryButton = styled(Button)`
  background-color: var(--grey-500);
  &:hover {
    background-color: var(--grey-600);
  }
`;

// ─── Inline Confirm Popover for destructive actions ─────────────
// Portaled to document.body: Td's backdrop-filter gives every cell its own
// stacking context, so an in-cell absolute popover paints under the rows
// below it and clips against the TableWrapper scroll overflow.
const Popover = styled.div`
  position: fixed;
  z-index: 5000;
  background: var(--color-surface);
  border: 1px solid var(--shadow-a08);
  box-shadow: 0 8px 24px var(--shadow-a15);
  border-radius: 10px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ConfirmDelete = ({ onConfirm, label = 'Delete', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top|bottom, right }, null until measured
  const anchorRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const btn = anchorRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      // Anchor scrolled offscreen: close instead of leaving a floating popover.
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      const gap = 6;
      const right = Math.max(10, window.innerWidth - rect.right);
      if (window.innerHeight - rect.bottom - gap < 60) {
        // No room below: bottom-anchor just above the button.
        setPos({ bottom: window.innerHeight - rect.top + gap, right });
      } else {
        setPos({ top: rect.bottom + gap, right });
      }
    };
    place();
    const onDocMouseDown = (e) => {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <>
      <DangerButton ref={anchorRef} type="button" aria-label={label} onClick={() => setOpen(true)} disabled={disabled}>
        {label}
      </DangerButton>
      {open &&
        createPortal(
          <Popover
            ref={popRef}
            role="dialog"
            aria-label="Confirm delete"
            style={{ ...pos, visibility: pos ? 'visible' : 'hidden', pointerEvents: pos ? 'auto' : 'none' }}
          >
            <span style={{ fontSize: '0.8rem', color: 'var(--grey-700)' }}>Confirm?</span>
            <DangerButton
              type="button"
              onClick={() => {
                setOpen(false);
                onConfirm && onConfirm();
              }}
            >
              Delete
            </DangerButton>
            <Button type="button" onClick={() => setOpen(false)} style={{ backgroundColor: 'var(--grey-500)' }}>Cancel</Button>
          </Popover>,
          document.body
        )}
    </>
  );
};

// ─── Generic inline Confirm Popover ─────────────────────────────────────────────
// Same portal scaffolding as ConfirmDelete (portal to body to escape the Td
// backdrop-filter stacking context, z-index 5000, flip-above when room below < 60px,
// outside-click + Escape dismiss, capture-phase scroll/resize reposition) but
// CONTROLLED by the consumer and neutral (not destructive): open it after a value is
// chosen, anchor it to the trigger, supply the message + confirm label. Used by the
// Customer Specs Manage table for inline status changes. ConfirmDelete is left intact.
export const ConfirmPopover = ({ open, anchorRef, message = 'Confirm?', confirmLabel = 'Confirm', onConfirm, onCancel }) => {
  const [pos, setPos] = useState(null);
  const popRef = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const place = () => {
      const el = anchorRef && anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) { cancelRef.current && cancelRef.current(); return; }
      const gap = 6;
      const right = Math.max(10, window.innerWidth - rect.right);
      if (window.innerHeight - rect.bottom - gap < 60) {
        setPos({ bottom: window.innerHeight - rect.top + gap, right });
      } else {
        setPos({ top: rect.bottom + gap, right });
      }
    };
    place();
    const onDocMouseDown = (e) => {
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      cancelRef.current && cancelRef.current();
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') cancelRef.current && cancelRef.current(); };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, anchorRef]);

  if (!open) return null;
  return createPortal(
    <Popover
      ref={popRef}
      role="dialog"
      aria-label="Confirm action"
      style={{ ...pos, visibility: pos ? 'visible' : 'hidden', pointerEvents: pos ? 'auto' : 'none' }}
    >
      <span style={{ fontSize: '0.8rem', color: 'var(--grey-700)' }}>{message}</span>
      <Button type="button" onClick={() => onConfirm && onConfirm()}>{confirmLabel}</Button>
      <Button type="button" onClick={() => onCancel && onCancel()} style={{ backgroundColor: 'var(--grey-500)' }}>Cancel</Button>
    </Popover>,
    document.body
  );
};

export const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--white-a66);
  border-radius: 8px;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--black);
  font-family: var(--font-body);
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border: 1px solid var(--green-800);
    box-shadow: 0 0 0 1px var(--green-800);
  }
`;

export const Select = styled.select.attrs(props => ({
  name: props.name,
  value: props.value,
  onChange: props.onChange
}))`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--white-a66);
  border-radius: 8px;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--black);
  font-family: var(--font-body);
  transition: all 0.2s ease;
  

  &:focus {
    outline: none;
    border: 1px solid var(--green-800);
    box-shadow: 0 0 0 1px var(--green-800);
  }
`;

export const TextArea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--white-a66);
  border-radius: 8px;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--black);
  font-family: var(--font-body);
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border: 1px solid var(--green-800);
    box-shadow: 0 0 0 1px var(--green-800);
  }
`;

// ─── GLOBAL PID STYLE PATCH ─────────────────────────────────────────────────────

export const GlobalPidStyles = createGlobalStyle`
  ${[...Array(10)].map((_, i) => `
    .pid-group-${i} {
      background-color: ${i % 2 === 0 ? 'var(--white-a15)' : 'var(--white-a25)'};
    }
  `).join('\n')}
`;

// ─── PAGE STRUCTURE ─────────────────────────────────────────────────────────────

export const PageHeader = styled.div`
  background: var(--green-a60);
  backdrop-filter: blur(8px);
  color: var(--white);
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 12px var(--shadow-a15);
`;

export const TableHeaderBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem 1rem;
  padding: 0.75rem 1rem;
  background: var(--white-a25);
  border-radius: 12px;
  backdrop-filter: blur(8px);
`;

export const SectionContainer = styled.div`
  background-color: var(--white-a25);
  border-radius: 12px;
  backdrop-filter: blur(8px);
  padding: 1rem 1.5rem;
  margin-bottom: 1rem;
  font-family: var(--font-body);
`;

export const GlassPanel = styled.div`
  position: fixed;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 800px;
  background: var(--white-a15);
  backdrop-filter: blur(20px);
  border: 1px solid var(--white-a20);
  box-shadow: 0 8px 32px var(--shadow-a25);
  border-radius: 16px;
  padding: 2rem;
  z-index: 999;
  color: ${tableColors.text};
  overflow-y: auto;
  overflow-x: hidden; /* added */
  max-height: 80vh;
`;
